
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motionService } from './services/motionService';
import { cameraMotionService } from './services/cameraMotionService';
import { audioEngine } from './services/audioEngine';
import { storageService } from './services/storageService';
import { analytics } from './services/analyticsService';
import { analyzeMindfulnessSession } from './services/mindfulnessService';
import { Visualizer } from './components/Visualizer';
import { Controls } from './components/Controls';
import { ControlDock } from './components/ControlDock';
import { CameraMotionHUD } from './components/CameraMotionHUD';
import { Tutorial } from './components/Tutorial';
import { LegalModal } from './components/LegalModal';
import { QRModal } from './components/QRModal';
import { MindfulnessModal } from './components/MindfulnessModal';
import { FeedbackModal } from './components/FeedbackModal';
import { MotionGuide } from './components/MotionGuide';
import { MotionData, AudioPreset, Recording, InteractionMode, MindfulnessReport, PerformanceMetrics } from './types';
import { DEFAULT_PRESETS } from './data/defaultPresets';

export default function App() {
  const [showLanding, setShowLanding] = useState(true);
  const [showModeSelection, setShowModeSelection] = useState(false);
  const [started, setStarted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [interactionMode, setInteractionMode] = useState<InteractionMode>(InteractionMode.MOTION);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isCameraSessionRunning, setIsCameraSessionRunning] = useState(false);
  const [isControlsOpen, setIsControlsOpen] = useState(false);
  const [isPerformanceActive, setIsPerformanceActive] = useState(false);
  const [showRecordHint, setShowRecordHint] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showMotionGuide, setShowMotionGuide] = useState(false);
  const [isZenMode, setIsZenMode] = useState(false);
  const [themeColor, setThemeColor] = useState(DEFAULT_PRESETS[0].colorHex || '#38bdf8');
  const [preset, setPreset] = useState<AudioPreset | null>(DEFAULT_PRESETS[0]);
  const [savedPresets, setSavedPresets] = useState<AudioPreset[]>(DEFAULT_PRESETS);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isArmingRecord, setIsArmingRecord] = useState(false);
  const [showLegal, setShowLegal] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  // Session Duration & Timer State
  const [sessionDuration, setSessionDuration] = useState<number>(0); // 0 = Free Play
  const [sessionElapsed, setSessionElapsed] = useState<number>(0);
  const [isFadingOut, setIsFadingOut] = useState<boolean>(false);
  const [isHUDMinimized, setIsHUDMinimized] = useState<boolean>(false);
  const [isCameraCountdownActive, setIsCameraCountdownActive] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Motion Sensitivity State (0.4x - 2.2x multiplier scaling input expressiveness)
  const [motionSensitivity, setMotionSensitivity] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('kinetic_motion_sensitivity');
      if (saved) {
        const parsed = parseFloat(saved);
        if (!isNaN(parsed) && parsed >= 0.2 && parsed <= 3.0) return parsed;
      }
    } catch(e) {}
    return 1.0;
  });

  useEffect(() => {
    motionService.setSensitivity(motionSensitivity);
    cameraMotionService.setSensitivity(motionSensitivity);
    try {
      localStorage.setItem('kinetic_motion_sensitivity', motionSensitivity.toString());
    } catch(e) {}
  }, [motionSensitivity]);

  // Mindfulness Report Modal State
  const [activeMindfulnessModal, setActiveMindfulnessModal] = useState<{
    isOpen: boolean;
    report: MindfulnessReport | null;
    recording: Recording | null;
    isLoading: boolean;
  }>({
    isOpen: false,
    report: null,
    recording: null,
    isLoading: false
  });
  const [analyzingRecordingId, setAnalyzingRecordingId] = useState<string | null>(null);

  const motionRef = useRef<MotionData>({ x: 0, y: 0, z: 0, rotAlpha: 0, rotBeta: 0, rotGamma: 0, intensity: 0, jerk: 0 });

  const isPerformanceActiveRef = useRef(false);
  const isMobileRef = useRef(false);
  const isControlsOpenRef = useRef(false);
  const showMotionGuideRef = useRef(false);
  const isCameraActiveRef = useRef(false);
  const isCameraSessionRunningRef = useRef(false);
  const interactionModeRef = useRef(interactionMode);
  const isFadingOutRef = useRef(false);
  const isRecordingRef = useRef(false);
  const sessionDurationRef = useRef(sessionDuration);
  const sessionElapsedRef = useRef(sessionElapsed);

  useEffect(() => { showMotionGuideRef.current = showMotionGuide; }, [showMotionGuide]);
  useEffect(() => { isCameraActiveRef.current = isCameraActive; }, [isCameraActive]);
  useEffect(() => { isCameraSessionRunningRef.current = isCameraSessionRunning; }, [isCameraSessionRunning]);
  useEffect(() => { interactionModeRef.current = interactionMode; }, [interactionMode]);
  useEffect(() => { isFadingOutRef.current = isFadingOut; }, [isFadingOut]);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);
  useEffect(() => { sessionDurationRef.current = sessionDuration; }, [sessionDuration]);
  useEffect(() => { sessionElapsedRef.current = sessionElapsed; }, [sessionElapsed]);

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage((cur) => (cur === msg ? null : cur));
    }, 4500);
  }, []);

  const triggerAutoFadeOutStop = useCallback(async () => {
    if (isFadingOutRef.current) return;
    setIsFadingOut(true);

    // Ring singing bowl chime to mark mindfully entering stillness
    audioEngine.playSingingBowlChime(216);

    // Smoothly fade out audio over 2.5 seconds
    await audioEngine.fadeOut(2.5);

    // If recording was active, automatically stop, finalize metrics, and save
    if (isRecordingRef.current) {
      const result = await audioEngine.stopRecording();
      if (result) {
        const durationTag = sessionDurationRef.current > 0
          ? `${Math.round(sessionDurationRef.current / 60)}m`
          : 'Timed';
        const newRecording: Recording = {
          id: crypto.randomUUID(),
          url: result.url,
          blob: result.blob,
          timestamp: Date.now(),
          name: `Session ${recordings.length + 1} (${durationTag})`,
          mimeType: result.mimeType,
          metrics: result.metrics
        };
        setRecordings(prev => [newRecording, ...prev]);
        try {
          await storageService.saveRecording(newRecording);
        } catch (e) {
          console.warn('Storage save recording error:', e);
        }
        showToast('✨ Practice Complete • Stored in Sound Library.');

        // Automatically generate and present the somatic mindfulness reflection
        setTimeout(() => {
          handleAnalyzeMindfulness(newRecording);
        }, 600);
      }
      setIsRecording(false);
    } else {
      showToast('🌿 Mindful Stillness • Session complete.');
    }

    setIsPerformanceActive(false);
    setIsCameraSessionRunning(false);
    setIsArmingRecord(false);
    setIsFadingOut(false);
    audioEngine.resetMasterVolume(0.35);
  }, [recordings.length, showToast]);

  // Session Duration Timer Interval (pauses while 3s preparation countdown is running)
  useEffect(() => {
    if (!started || !isPerformanceActive || isFadingOut || isCameraCountdownActive) return;

    const timer = setInterval(() => {
      setSessionElapsed((prev) => {
        const next = prev + 1;
        const target = sessionDurationRef.current;
        if (target > 0 && next >= target) {
          triggerAutoFadeOutStop();
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [started, isPerformanceActive, isFadingOut, isCameraCountdownActive, triggerAutoFadeOutStop]);

  useEffect(() => {
    const mobileCheck = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    setIsMobile(mobileCheck);
    isMobileRef.current = mobileCheck;

    const checkSensors = async () => {
      const status = await motionService.checkAvailability();
      if (!mobileCheck) setInteractionMode(InteractionMode.MOTION);
      else if (!status.supported) setInteractionMode(InteractionMode.TOUCH);
    };
    checkSensors();

    const loadPersistence = async () => {
      try {
        const storedRecordings = await storageService.getAllRecordings();
        setRecordings(storedRecordings);
      } catch (error) {}
    };
    loadPersistence();

    // Subscribe to Camera motion events
    const unsubCamera = cameraMotionService.subscribeMotion((data) => {
      if (interactionModeRef.current === InteractionMode.CAMERA || isCameraActiveRef.current) {
        motionRef.current = data;
        // Only drive the audio synthesizer if the camera session is actively running and no overlay modals are active
        if (isPerformanceActiveRef.current && !isControlsOpenRef.current && !showMotionGuideRef.current && isCameraSessionRunningRef.current) {
          audioEngine.update(data);
        }
      }
    });

    // Subscribe to Camera tracking state to pause timer & coordinate countdown
    const unsubCameraState = cameraMotionService.subscribeState((state) => {
      setIsCameraCountdownActive(state.isCountdownActive);
    });

    return () => {
      unsubCamera();
      unsubCameraState();
      cameraMotionService.stopCamera();
      motionService.stop();
      audioEngine.suspend();
    };
  }, []);

  useEffect(() => { isPerformanceActiveRef.current = isPerformanceActive; }, [isPerformanceActive]);
  useEffect(() => { isControlsOpenRef.current = isControlsOpen; }, [isControlsOpen]);

  useEffect(() => {
    if (started) {
      if (!isControlsOpen) {
        audioEngine.resume();
        setIsPerformanceActive(true);
      }
    }
  }, [isControlsOpen, started]);

  // Auto-resume camera tracking and audio engine when window/preview is restored
  useEffect(() => {
    const handleRestore = async () => {
      if (document.visibilityState === 'visible') {
        if (started) {
          await audioEngine.resume();
          if (!isControlsOpenRef.current) {
            setIsPerformanceActive(true);
          }
          if (isCameraActiveRef.current || interactionModeRef.current === InteractionMode.CAMERA) {
            await cameraMotionService.resumeCamera();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleRestore);
    window.addEventListener('focus', handleRestore);
    window.addEventListener('pageshow', handleRestore);
    window.addEventListener('resize', handleRestore);

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) return;
      if (e.key === 'z' || e.key === 'Z') {
        setIsZenMode(prev => !prev);
      } else if (e.key === 'Escape' && isZenMode) {
        setIsZenMode(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('visibilitychange', handleRestore);
      window.removeEventListener('focus', handleRestore);
      window.removeEventListener('pageshow', handleRestore);
      window.removeEventListener('resize', handleRestore);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [started, isZenMode]);

  const handleToggleCamera = (forceState?: boolean) => {
    const nextState = forceState !== undefined ? forceState : !isCameraActive;
    setIsCameraActive(nextState);
    if (nextState) {
      setInteractionMode(InteractionMode.CAMERA);
      setIsCameraSessionRunning(false);
      setIsArmingRecord(false);
    } else {
      cameraMotionService.stopCamera();
      setIsCameraSessionRunning(false);
      setIsArmingRecord(false);
      const defaultMode = isMobile ? InteractionMode.TOUCH : InteractionMode.MOTION;
      setInteractionMode(defaultMode);
      motionService.setMode(defaultMode);
    }
  };

  const handleStartCameraSession = (opts?: { record?: boolean }) => {
    const shouldRecord = opts?.record ?? isArmingRecord;
    setIsArmingRecord(shouldRecord);

    // Trigger 3-second preparation countdown overlay
    cameraMotionService.startCountdown(3);

    // When countdown completes or is skipped, launch audio session and recording if armed
    cameraMotionService.onCountdownComplete(() => {
      setIsCameraSessionRunning(true);
      setIsPerformanceActive(true);
      setSessionElapsed(0);
      audioEngine.resume();

      if (shouldRecord) {
        audioEngine.startRecording();
        setIsRecording(true);
        setIsArmingRecord(false);
        showToast('⏺️ Recording Started • Live audio capture active');
      } else {
        showToast('🖐️ Movement Active • Move your hands to sculpt music');
      }
    });
  };

  const handleInteractionModeChange = (mode: InteractionMode) => {
    if (mode === InteractionMode.CAMERA) {
      handleToggleCamera(true);
    } else {
      handleToggleCamera(false);
      setInteractionMode(mode);
      motionService.setMode(mode);
    }
  };

  const handleSelectSessionDuration = useCallback((sec: number) => {
    setSessionDuration(sec);
    setSessionElapsed(0);

    if (sec > 0) {
      const mins = Math.round(sec / 60);
      showToast(`⏱️ ${mins} min session set • Smooth fadeout on completion`);
    } else {
      showToast('♾️ Open Practice • Continuous flow');
    }
  }, [showToast]);

  const initExperience = async (selectedMode: InteractionMode) => {
    if (isInitializing) return;
    
    // User Gesture Hook for Web Audio - initialize & resume synchronously on touch
    audioEngine.init();
    audioEngine.resume();
    
    setIsInitializing(true);
    setInteractionMode(selectedMode);
    motionService.setMode(selectedMode);
    
    try {
      let motionGranted = true;
      if (isMobile && selectedMode === InteractionMode.MOTION) {
        motionGranted = await motionService.requestPermission();
      }
      
      if (motionGranted || selectedMode === InteractionMode.TOUCH || selectedMode === InteractionMode.CAMERA || !isMobile) {
        if (!preset) {
          const initial = DEFAULT_PRESETS[0];
          audioEngine.setPreset(initial);
          setPreset(initial);
        }

        if (selectedMode === InteractionMode.CAMERA) {
          setIsCameraActive(true);
          setIsCameraSessionRunning(false);
          setIsArmingRecord(false);
        }

        motionService.start();
        motionService.subscribe((data) => {
          if (interactionModeRef.current !== InteractionMode.CAMERA && !isCameraActiveRef.current) {
            motionRef.current = data;
            if (isPerformanceActiveRef.current && !isControlsOpenRef.current && !showMotionGuideRef.current) {
              audioEngine.update(data);
            }
          }
        });
        
        setStarted(true);
        setShowModeSelection(false);
        setIsInitializing(false);
        setIsControlsOpen(false);
        
        // Auto-activate performance for standard non-camera modes across Mobile and Desktop
        if (selectedMode !== InteractionMode.CAMERA) {
          setIsPerformanceActive(true);
        }

        if (!localStorage.getItem('hasSeenTutorial')) setShowTutorial(true);
      } else {
        throw new Error("Permission denied");
      }
    } catch (error) {
      console.error("Initialization notice:", error);
      setIsInitializing(false);
      if (selectedMode === InteractionMode.MOTION && isMobile) {
        showToast("📱 Motion sensors restricted. Switching to Touch Screen Mode.");
        initExperience(InteractionMode.TOUCH);
      } else {
        showToast("Audio ready • Tap to begin playing");
        setIsPerformanceActive(true);
      }
    }
  };

  const handleStartLanding = (mode: InteractionMode = InteractionMode.MOTION) => {
    // Prime Web Audio on direct user tap from landing
    audioEngine.init();
    audioEngine.resume();
    setShowLanding(false);
    if (mode === InteractionMode.CAMERA) {
      initExperience(InteractionMode.CAMERA);
    } else if (isMobile) {
      setShowModeSelection(true);
    } else {
      initExperience(InteractionMode.MOTION);
    }
  };

  const toggleRecording = async () => {
    if (isRecording) {
      const result = await audioEngine.stopRecording();
      if (result) {
        const durationTag = sessionDuration > 0
          ? `${Math.round(sessionDuration / 60)}m`
          : 'Live';
        const newRecording: Recording = {
          id: crypto.randomUUID(),
          url: result.url,
          blob: result.blob,
          timestamp: Date.now(),
          name: `Session ${recordings.length + 1} (${durationTag})`,
          mimeType: result.mimeType,
          metrics: result.metrics
        };
        setRecordings(prev => [newRecording, ...prev]);
        try { await storageService.saveRecording(newRecording); } catch (e) {}
        showToast('✨ Recording Saved to Sound Library.');
      }
      setIsRecording(false);
      if (!isMobile && !isCameraActive) setIsPerformanceActive(false);
    } else {
      // In Camera Mode: if session hasn't started yet, toggle arm recording for setup
      if (isCameraActive && !isCameraSessionRunning) {
        const nextArm = !isArmingRecord;
        setIsArmingRecord(nextArm);
        if (nextArm) {
          showToast('🎙️ Recording armed • Select timer & tap Start Session');
        } else {
          showToast('Recording disarmed');
        }
        return;
      }

      if (!isMobile && !localStorage.getItem('hasSeenRecordHint')) {
        setShowRecordHint(true);
        localStorage.setItem('hasSeenRecordHint', 'true');
      }

      if (isCameraActive) {
        // If camera session is already actively running, start live recording immediately
        audioEngine.startRecording();
        setIsRecording(true);
        showToast('⏺️ Recording Started');
      } else {
        // Standard (Cursor/Touch/Gyro) mode recording start
        audioEngine.startRecording();
        setIsRecording(true);
        setSessionElapsed(0);
        setIsPerformanceActive(true);
        audioEngine.resume();
        showToast('⏺️ Recording Started');
      }
    }
  };

  const handleAnalyzeMindfulness = async (recording: Recording) => {
    // If report already cached on recording, open instantly
    if (recording.mindfulnessReport) {
      setActiveMindfulnessModal({
        isOpen: true,
        report: recording.mindfulnessReport,
        recording,
        isLoading: false
      });
      return;
    }

    // Open modal in loading state & mark analyzing
    setActiveMindfulnessModal({
      isOpen: true,
      report: null,
      recording,
      isLoading: true
    });
    setAnalyzingRecordingId(recording.id);

    try {
      // Build or retrieve metrics
      const metrics: PerformanceMetrics = recording.metrics || {
        durationSeconds: 24,
        presetName: preset?.name || 'Ambient',
        presetCategory: preset?.category || 'Ambient',
        averageIntensity: 0.35,
        peakIntensity: 0.65,
        tempoStability: 82,
        smoothnessScore: 88,
        totalNotesTriggered: 18,
        jerkCount: 1
      };

      const report = await analyzeMindfulnessSession(metrics, recording.name);

      // Save report to recording state and storage
      const updatedRecording: Recording = {
        ...recording,
        metrics,
        mindfulnessReport: report
      };

      setRecordings(prev => prev.map(r => (r.id === recording.id ? updatedRecording : r)));
      try {
        await storageService.saveRecording(updatedRecording);
      } catch (e) {
        console.warn('Could not save updated mindfulness report to storage:', e);
      }

      setActiveMindfulnessModal({
        isOpen: true,
        report,
        recording: updatedRecording,
        isLoading: false
      });
    } catch (err) {
      console.error('Failed to analyze mindfulness session:', err);
      setActiveMindfulnessModal(prev => ({
        ...prev,
        isLoading: false
      }));
    } finally {
      setAnalyzingRecordingId(null);
    }
  };

  const handleStageClick = () => {
    if (!isMobile && started && !isControlsOpen) {
      setIsPerformanceActive(prev => !prev);
    }
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-slate-900 relative font-sans">
      <Visualizer 
        color={themeColor} 
        interactionMode={interactionMode} 
        isActive={started && !showModeSelection && !showLanding}
        onStageClick={handleStageClick}
        isPerformanceActive={isPerformanceActive || isMobile}
      />
      
      {showTutorial && (
        <Tutorial 
          mode={interactionMode} 
          isMobile={isMobile} 
          onSelectMode={(m) => handleInteractionModeChange(m)}
          onOpenMotionGuide={() => {
            setShowTutorial(false);
            setShowMotionGuide(true);
          }}
          onClose={() => {
            setShowTutorial(false);
            localStorage.setItem('hasSeenTutorial', 'true');
          }} 
        />
      )}
      
      {showQR && <QRModal onClose={() => setShowQR(false)} />}
      
      {showLegal && (
        <LegalModal 
          isFirstVisit={!started} 
          onAccept={() => { localStorage.setItem('hasAcceptedLegal', 'true'); setShowLegal(false); initExperience(interactionMode); }} 
          onClose={() => setShowLegal(false)}
        />
      )}

      {showRecordHint && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-6">
          <div className="bg-slate-900 border border-white/10 p-8 rounded-[2.5rem] max-w-sm w-full text-center space-y-6 shadow-2xl">
            <h3 className="text-xl font-bold text-white uppercase italic tracking-tight">Perform Mode Required</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              Music only plays on desktop when you actively conduct. <br/><br/>
              <strong>Left-Click the screen</strong> to start your melody.
            </p>
            <button onClick={() => setShowRecordHint(false)} className="w-full py-4 bg-indigo-600 text-white rounded-xl font-black uppercase tracking-widest text-xs transition active:scale-95">Start Performance</button>
          </div>
        </div>
      )}

      {started && !showLanding && !showModeSelection && (
        <>
          {/* Zen Mode Top Exit Pill */}
          {isZenMode ? (
            <div className="fixed top-3 sm:top-6 left-1/2 -translate-x-1/2 z-50 animate-fade-in pointer-events-auto">
              <button
                id="exit-zen-mode-pill-btn"
                onClick={() => setIsZenMode(false)}
                className="px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-slate-950/85 hover:bg-slate-900 border border-emerald-500/40 text-slate-200 hover:text-white text-xs sm:text-sm font-semibold backdrop-blur-xl shadow-2xl flex items-center gap-2.5 transition active:scale-95 group cursor-pointer min-h-[44px]"
                title="Exit Zen Focus Mode (or press Z)"
              >
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-sm shadow-emerald-400" />
                <span>🌿 Zen Mode • Press [Z] or Click to Restore</span>
              </button>
            </div>
          ) : (
            <>
              <header 
                role="banner" 
                className="fixed top-3 sm:top-6 left-3 sm:left-6 z-40 flex items-center gap-2 sm:gap-3 pointer-events-auto max-w-[calc(100vw-2rem)]"
              >
                {/* Active Conductor Source Pill (Interactive Guide Trigger) */}
                <button
                  id="active-conductor-pill-btn"
                  onClick={() => setShowMotionGuide(true)}
                  className="flex items-center gap-2 sm:gap-2.5 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-full bg-slate-950/80 hover:bg-slate-900 text-slate-200 hover:text-white border border-white/20 backdrop-blur-xl shadow-xl transition active:scale-95 text-xs sm:text-sm font-semibold group shrink-0 cursor-pointer min-h-[44px]"
                  title="How Physical Movement Conducts Sound (Click to View Motion Map)"
                >
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                    isCameraActive ? 'bg-sky-400 animate-pulse shadow-sm shadow-sky-400' : (isMobile ? 'bg-purple-400' : 'bg-indigo-400')
                  }`} />
                  <span className="font-bold text-white">
                    {isCameraActive 
                      ? '🖐️ Webcam Body' 
                      : (isMobile ? '📱 Phone Tilt' : '🖱️ Cursor Motion')}
                  </span>
                  <span className="text-xs text-slate-400 group-hover:text-slate-200 hidden sm:inline border-l border-white/15 pl-2">
                    Motion Guide ?
                  </span>
                </button>
              </header>

              {/* Top Right Stage Action Pill (User Feedback) */}
              <div className="fixed top-3 sm:top-6 right-3 sm:right-6 z-40 flex items-center gap-2 pointer-events-auto">
                <button
                  id="stage-feedback-btn"
                  onClick={() => setShowFeedback(true)}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full bg-slate-950/85 hover:bg-slate-900 text-slate-200 hover:text-white border border-amber-500/35 hover:border-amber-400/70 backdrop-blur-xl shadow-xl transition active:scale-95 text-xs sm:text-sm font-semibold group cursor-pointer min-h-[44px]"
                  title="Rate and Share Feedback (Google Form)"
                >
                  <span className="text-amber-400 group-hover:scale-110 transition-transform">⭐</span>
                  <span className="font-bold text-white">Feedback</span>
                </button>
              </div>
            </>
          )}

          {/* Unified Bottom Control Dock */}
          <ControlDock 
            onOpenSoundLab={() => setIsControlsOpen(true)}
            currentPresetName={preset?.name || DEFAULT_PRESETS[0].name}
            presetColor={themeColor}
            isRecording={isRecording}
            onToggleRecording={toggleRecording}
            isCameraActive={isCameraActive}
            onToggleCamera={() => handleToggleCamera()}
            onOpenMotionGuide={() => setShowMotionGuide(true)}
            isZenMode={isZenMode}
            onToggleZenMode={() => {
              setIsZenMode(true);
              showToast('🌿 Zen Mode active • Press [Z] or tap anytime to exit');
            }}
            isMobile={isMobile}
            interactionMode={interactionMode}
            isSoundLabOpen={isControlsOpen}
            motionSensitivity={motionSensitivity}
            onChangeSensitivity={setMotionSensitivity}
          />
        </>
      )}

      {/* Floating Notification Toast */}
      {toastMessage && !isZenMode && (
        <div 
          id="session-toast-banner"
          className="fixed bottom-20 sm:bottom-24 left-1/2 -translate-x-1/2 z-[200] px-4 sm:px-5 py-2.5 sm:py-3 rounded-full bg-slate-900/95 text-white text-xs font-semibold border border-white/20 shadow-2xl backdrop-blur-md flex items-center gap-2.5 animate-scale-in max-w-[calc(100vw-2rem)]"
        >
          <span className="truncate">{toastMessage}</span>
          <button 
            onClick={() => setToastMessage(null)}
            className="text-slate-400 hover:text-white p-1 rounded-full hover:bg-white/10 transition shrink-0"
            aria-label="Close notification"
          >
            ✕
          </button>
        </div>
      )}

      {/* Floating Camera Skeleton HUD */}
      <CameraMotionHUD 
        isActive={isCameraActive} 
        onToggleActive={() => handleToggleCamera(false)} 
        accentColor={themeColor}
        isMobile={isMobile}
        sessionDuration={sessionDuration}
        onChangeDuration={handleSelectSessionDuration}
        sessionElapsed={sessionElapsed}
        isFadingOut={isFadingOut}
        isRecording={isRecording}
        isArmingRecord={isArmingRecord}
        onToggleArmRecord={() => setIsArmingRecord(prev => !prev)}
        isSessionRunning={isCameraSessionRunning}
        onStartSession={handleStartCameraSession}
        onTriggerRecord={toggleRecording}
        onStopSession={triggerAutoFadeOutStop}
        isZenMode={isZenMode}
        onOpenMotionGuide={() => setShowMotionGuide(true)}
        isMinimized={isHUDMinimized}
        onToggleMinimized={setIsHUDMinimized}
      />

      {showLanding && (
        <div className="absolute inset-0 z-[150] bg-slate-950 flex flex-col items-center justify-center p-8 text-center animate-fade-in">
          <div className="max-w-6xl w-full z-10 flex flex-col md:flex-row items-center justify-center gap-16">
            <div className="flex-1 space-y-6 text-left">
              <h1 className="text-5xl md:text-7xl font-black text-[#38bdf8] tracking-widest uppercase leading-tight italic">KINETIC<br/>HARMONY</h1>
              <p className="text-slate-400 text-lg leading-relaxed font-medium">Generative music powered by your physical movement & webcam AI tracking.</p>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button 
                  onClick={() => handleStartLanding(InteractionMode.MOTION)} 
                  className="px-8 py-4 bg-[#5046e5] hover:bg-[#4338ca] text-white rounded-full font-bold shadow-xl transition-all active:scale-95 text-base flex items-center justify-center gap-2"
                >
                  <span>Start Session</span>
                  {!isMobile && <span className="text-xs opacity-75 font-normal">(Cursor / Sensors)</span>}
                </button>

                <button 
                  onClick={() => handleStartLanding(InteractionMode.CAMERA)} 
                  className="px-8 py-4 bg-sky-600 hover:bg-sky-500 text-white rounded-full font-bold shadow-xl transition-all active:scale-95 text-base flex items-center justify-center gap-2 border border-sky-400/40"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                  <span>Webcam Camera Mode</span>
                </button>
              </div>
            </div>
            {!isMobile && <div className="hidden md:block flex-1 bg-slate-900/60 p-10 rounded-[3rem] border border-white/10 text-center flex flex-col items-center justify-center"><QRModal onClose={() => {}} inline /></div>}
          </div>
        </div>
      )}

      {showModeSelection && (
        <div className="absolute inset-0 z-[100] bg-slate-950/90 backdrop-blur-xl flex flex-col items-center justify-center p-8 text-center">
          <div className="max-w-md w-full space-y-6">
            <h1 className="text-3xl font-black text-white uppercase italic">Controller Mode</h1>

            <div className="grid grid-cols-1 gap-3">
              <button onClick={() => initExperience(InteractionMode.CAMERA)} className="p-5 rounded-[1.75rem] border-2 bg-slate-900 border-sky-500/40 hover:border-sky-400 transition-all text-left flex items-start gap-3.5 shadow-lg shadow-sky-950/40">
                <div className="p-2 rounded-xl bg-sky-500/20 text-sky-300 shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white mb-0.5">Camera Motion Tracker</h3>
                  <p className="text-xs text-slate-400">Conduct hands-free using your webcam to track skeletal body & hand movements.</p>
                </div>
              </button>
              <button onClick={() => initExperience(InteractionMode.MOTION)} className="p-5 rounded-[1.75rem] border-2 bg-slate-900 border-white/10 hover:border-indigo-500 transition-all text-left flex items-start gap-3.5">
                <div className="p-2 rounded-xl bg-indigo-500/20 text-indigo-300 shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white mb-0.5">Gyro Motion Mode</h3>
                  <p className="text-xs text-slate-400">Tilt and turn your phone to shape the sound.</p>
                </div>
              </button>
              <button onClick={() => initExperience(InteractionMode.TOUCH)} className="p-5 rounded-[1.75rem] border-2 bg-slate-900 border-white/10 hover:border-purple-500 transition-all text-left flex items-start gap-3.5">
                <div className="p-2 rounded-xl bg-purple-500/20 text-purple-300 shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672ZM12 2.25V4.5m5.834.166-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243-1.59-1.59" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white mb-0.5">Touch Screen Mode</h3>
                  <p className="text-xs text-slate-400">Tap and slide on the visualizer to sculpt music.</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {started && (
        <Controls 
          isOpen={isControlsOpen}
          onToggle={setIsControlsOpen}
          currentPresetName={preset?.name || DEFAULT_PRESETS[0].name}
          savedPresets={savedPresets}
          onSelectPreset={(p) => { 
            audioEngine.setPreset(p); 
            setPreset(p); 
            if (p.colorHex) setThemeColor(p.colorHex);
          }}
          recordings={recordings}
          onDeleteRecording={(id) => setRecordings(prev => prev.filter(r => r.id !== id))}
          onOpenTutorial={() => setShowTutorial(true)}
          onOpenLegal={() => setShowLegal(true)}
          interactionMode={interactionMode}
          onInteractionModeChange={handleInteractionModeChange}
          isMobile={isMobile}
          onOpenQR={() => setShowQR(true)}
          onOpenFeedback={() => setShowFeedback(true)}
          onAnalyzeMindfulness={handleAnalyzeMindfulness}
          analyzingRecordingId={analyzingRecordingId}
          isZenMode={isZenMode}
          isCameraActive={isCameraActive}
          isHUDMinimized={isHUDMinimized}
          onOpenMotionGuide={() => setShowMotionGuide(true)}
        />
      )}

      {/* Movement to Sound Synthesis Mapping Guide */}
      <MotionGuide
        interactionMode={interactionMode}
        isCameraActive={isCameraActive}
        isOpen={showMotionGuide}
        onClose={() => setShowMotionGuide(false)}
        onSelectMode={(m) => handleInteractionModeChange(m)}
      />

      {/* Mindfulness & Somatic Reflection Modal */}
      <MindfulnessModal
        isOpen={activeMindfulnessModal.isOpen}
        onClose={() => setActiveMindfulnessModal(prev => ({ ...prev, isOpen: false }))}
        report={activeMindfulnessModal.report}
        recording={activeMindfulnessModal.recording}
        isLoading={activeMindfulnessModal.isLoading}
        onOpenFeedback={() => setShowFeedback(true)}
      />

      {/* Google Form User Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
      />
    </div>
  );
}
